import { Placeholder } from '@tiptap/extensions';
import type { Extensions } from '@tiptap/core';

/**
 * The slice of the tiptap editor this needs.
 *
 * `extensionManager` is nullable here even though tiptap's own types declare it
 * as always present: `Editor.destroy()` nulls it at runtime, so the declaration
 * is a lie we have to defend against rather than trust.
 */
type PlaceholderSyncEditor = {
  readonly isDestroyed: boolean;
  readonly extensionManager: { readonly extensions: Extensions } | null;
  setOptions: (options: { extensions: Extensions }) => void;
};

/**
 * Swap the placeholder extension's text on a live editor.
 *
 * The destroyed check is load-bearing, not defensive: `useEditor` arms a
 * destruction timer during render and only cancels it once its own effect marks
 * the component as mounted. React flushes passive effects after paint, so on a
 * slow mount the timer fires first and tiptap tears down the very instance the
 * committed render handed us. Reading through it then throws, and the error
 * boundary takes the whole page down with it.
 */
export function syncPlaceholder(editor: PlaceholderSyncEditor | null, placeholderText: string): void {
  if (editor == null || editor.isDestroyed || editor.extensionManager == null) return;

  editor.setOptions({
    extensions: editor.extensionManager.extensions.map((extension) => {
      if (extension.name === 'placeholder') {
        return Placeholder.configure({ placeholder: placeholderText });
      }
      return extension;
    }),
  });
}
